// WhatNextPanel — Document-level AI-suggested actions list
import { useState, useEffect, useCallback } from 'react'
import {
  Shield, Clipboard, BookOpen, Link, Sparkles, Globe,
  Zap, Factory, TrendingUp, RefreshCw, CheckCircle, X,
  Users, Building2, MapPin
} from 'lucide-react'
import { getWhatNext, refreshWhatNext, updateWhatNextAction } from '../../services/api'
import type { SuggestedAction, SuggestedActionType, DirectoryMatch } from '../../types'

interface WhatNextPanelProps {
  documentId: string
  projectId?: string
}

const ACTION_ICONS: Record<SuggestedActionType, React.ElementType> = {
  ip_protection: Shield,
  prior_art: Clipboard,
  peer_review: Clipboard,
  journal: BookOpen,
  crossref: Link,
  hidden: Sparkles,
  cross_domain: Globe,
  specialist: Zap,
  manufacturer: Factory,
  investor: TrendingUp,
  cross_pollination: RefreshCw,
  coauthor_network: Users,
  institution_cluster: Building2,
  geo_proximity: MapPin,
}

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
}

function getPriorityLabel(priority: number): { label: string; color: string } {
  if (priority <= 3) return { label: 'High', color: PRIORITY_COLORS.high }
  if (priority <= 7) return { label: 'Medium', color: PRIORITY_COLORS.medium }
  return { label: 'Low', color: PRIORITY_COLORS.low }
}

const SCOPE_COLORS: Record<string, string> = {
  document: 'bg-blue-100 text-blue-700',
  project: 'bg-orange-100 text-orange-700',
  ecosystem: 'bg-purple-100 text-purple-700',
}

function DirectoryMatchCard({ match }: { match: DirectoryMatch }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 bg-emerald-50 rounded text-xs border border-emerald-200">
      <div className="flex-1 min-w-0">
        <span className="font-medium text-emerald-900">{match.name}</span>
        {match.institution && (
          <span className="text-emerald-600 ml-1">({match.institution})</span>
        )}
        <div className="flex items-center gap-2 text-emerald-500 mt-0.5">
          {match.paper_count > 0 && <span>{match.paper_count} papers</span>}
          {match.location && <span>{match.location}</span>}
          {match.distance_km !== undefined && <span>{match.distance_km}km away</span>}
          {/* BUG-NEW-Q (Task #2719): show the match rationale explicitly
              instead of a generic score band. Users need to know why a
              match was suggested, not just how strong it is. */}
          <span className="text-[10px] opacity-70">{
            Number(match.match_score) >= 0.80 ? `Strong match — ${match.paper_count > 0 ? 'overlapping publications' : 'entity profile'}` :
            Number(match.match_score) >= 0.65 ? `Good match — ${match.institution ? 'institution alignment' : 'topic overlap'}` :
            Number(match.match_score) >= 0.55 ? 'Possible match — weak signal, verify before outreach' : `score: ${match.match_score}`
          }</span>
        </div>
      </div>
      {/* BUG-NEW-Q (Task #2719): provenance label replaced generic
          "From Directory" with source-specific mapping. Currently only
          'directory' source exists on DirectoryMatch; future sources
          (ecosystem, academic, linkedin, etc.) should map here.  */}
      <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700">
        {match.source === 'directory' ? 'Scholar directory' : 'External source'}
      </span>
    </div>
  )
}

// BUG-NEW-Q (Task #2719): CopyAllDirectoryMatches removed from the UI.
// The backend directory/copy_handler.py:65-100 is strictly single-entity
// (one CRM row per call), so a client-side bulk clipboard copy bypassed
// the backend provenance + dedup gates. Users now copy per-match via the
// individual row actions ("Add to CRM" button on each DirectoryMatchCard
// — out of scope here; TODO in a future UI task). The one-click bulk
// copy path is removed to prevent accidental mass CRM additions.
//
// function CopyAllDirectoryMatches({ matches }: { matches: DirectoryMatch[] }) {
//   ... removed ...
// }
function CopyAllDirectoryMatches(_: { matches: DirectoryMatch[] }) {
  return null
}

export default function WhatNextPanel({ documentId }: WhatNextPanelProps) {
  const [actions, setActions] = useState<SuggestedAction[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  // BUG-121: the refresh/accept/dismiss handlers used to `catch { /* ignore */ }`,
  // so a failed action left the UI unchanged with no signal. Surface it.
  const [actionError, setActionError] = useState<string | null>(null)

  const fetchActions = useCallback(() => {
    setLoading(true)
    getWhatNext(documentId)
      .then(setActions)
      .catch(() => setActions([]))
      .finally(() => setLoading(false))
  }, [documentId])

  useEffect(() => { fetchActions() }, [fetchActions])

  const handleRefresh = async () => {
    setRefreshing(true)
    setActionError(null)
    try {
      const refreshed = await refreshWhatNext(documentId)
      setActions(refreshed)
    } catch {
      setActionError('Could not refresh suggestions. Please try again.')
    }
    setRefreshing(false)
  }

  const handleAccept = async (action: SuggestedAction) => {
    setActionError(null)
    try {
      await updateWhatNextAction(documentId, action.action_id, 'accepted')
      setActions((prev) => prev.map((a) =>
        a.action_id === action.action_id ? { ...a, status: 'accepted' } : a
      ))
    } catch {
      setActionError('Could not accept that suggestion. Please try again.')
    }
  }

  const handleDismiss = async (action: SuggestedAction) => {
    setActionError(null)
    try {
      await updateWhatNextAction(documentId, action.action_id, 'dismissed')
      setActions((prev) => prev.map((a) =>
        a.action_id === action.action_id ? { ...a, status: 'dismissed' } : a
      ))
    } catch {
      setActionError('Could not dismiss that suggestion. Please try again.')
    }
  }

  const pendingActions = actions.filter((a) => a.status === 'pending')
  const completedActions = actions.filter((a) => a.status !== 'pending')

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Loading suggestions...</div>
  }

  return (
    <div className="space-y-3">
      {actionError && (
        <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          {actionError}
        </p>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          AI Suggestions ({pendingActions.length} pending)
        </h3>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1 px-2 py-1 text-xs text-sky-600 hover:text-sky-800 hover:bg-sky-50 rounded"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {pendingActions.length === 0 && (
        <p className="text-sm text-gray-500 italic">No pending suggestions. Upload or analyze a document to generate suggestions.</p>
      )}

      {/* Pending actions */}
      {pendingActions.map((action) => {
        const Icon = ACTION_ICONS[action.action_type] || Zap
        const { label: priorityLabel, color: priorityColor } = getPriorityLabel(action.priority)
        const scopeColor = SCOPE_COLORS[action.scope] || SCOPE_COLORS.document
        const isHidden = action.action_type === 'hidden'
        const hasDirectoryMatches = Array.isArray(action.directory_matches) && action.directory_matches.length > 0
        const isMarketplace = action.scope === 'ecosystem' && !hasDirectoryMatches

        return (
          <div
            key={action.action_id}
            className={`border rounded-lg p-3 space-y-2 ${isHidden ? 'border-l-4 border-l-purple-400' : ''}`}
          >
            <div className="flex items-start gap-2">
              <Icon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">{action.title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${priorityColor}`}>
                    {priorityLabel}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${scopeColor}`}>
                    {action.scope}
                  </span>
                  {hasDirectoryMatches && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700">
                      {/* BUG-NEW-Q (Task #2719): specific provenance label. */}
                      Scholar directory
                    </span>
                  )}
                  {isMarketplace && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-600">
                      InnovateFlow marketplace
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1">{action.description}</p>
                <p className="text-xs text-purple-600 italic mt-1">{action.why_explanation}</p>
              </div>
            </div>

            {/* Directory entity matches */}
            {hasDirectoryMatches && (
              <div className="pl-6 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-medium text-emerald-700 uppercase tracking-wide">
                    Directory Matches ({action.directory_matches!.length})
                  </span>
                  <CopyAllDirectoryMatches matches={action.directory_matches!} />
                </div>
                {action.directory_matches!.map((match) => (
                  <DirectoryMatchCard key={match.entity_id} match={match} />
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 pl-6">
              <button
                onClick={() => handleAccept(action)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-sky-500 text-white rounded hover:bg-sky-600"
              >
                <CheckCircle className="w-3 h-3" /> Create Todo
              </button>
              <button
                onClick={() => handleDismiss(action)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              >
                <X className="w-3 h-3" /> Dismiss
              </button>
            </div>
          </div>
        )
      })}

      {/* Completed/dismissed actions (collapsed) */}
      {completedActions.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700">
            {completedActions.length} resolved action(s)
          </summary>
          <div className="mt-2 space-y-1">
            {completedActions.map((a) => (
              <div key={a.action_id} className="flex items-center gap-2 text-gray-500">
                {a.status === 'accepted' ? (
                  <CheckCircle className="w-3 h-3 text-green-400" />
                ) : (
                  <X className="w-3 h-3 text-gray-300" />
                )}
                <span className={a.status === 'accepted' ? 'line-through' : 'line-through'}>{a.title}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
